import React, { lazy } from "react";
import { ProviderContext, withSnackbar } from "notistack";
import { Stack } from "@mui/material";
import "./app.css";
import { app_singleton } from "./utility/app_singleton";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom-v5-compat";
import ThemeProvider from "./utility/theme";
import { AuthProvider } from "./auth/AuthContext";
import ProtectedRoute from "./auth/ProtectedRoute";
import { VerticalNavBar } from "./components/appbarnew/VerticalNavBar";
import Confirm_popup from "./components/confirm_popup/confirm_popup";
import PopupPrenotazione from "./pages/ordinazione/prenotazioni/popup_prenotazione";
import LoadingPopup from "./components/loading_popup/loading_popup";
import Close_popup from "./components/close_popup/close_popup";
import { Login } from "./pages/login/login";
import CassaincloudRegistrationInfoLayout from "./pages/restaurantAdmin/cassaincloudRegistration/CassaincloudRegistrationInfoLayout";
import { EventTrackerProvider } from "./eventtracker/eventTrackerContext";
import TrackedEventService from "./services/EventTrackerService";

// Lazy-loaded route components
const ChatLayout = lazy(() => import("./pages/chat/ChatLayout"));
const TabellaAnagraficaArticolo = lazy(() => import("./pages/restaurantAdmin/business_intelligence/anagrafica_articolo/TabellaAnagraficaArticolo"));
const InserimentoArticolo = lazy(() => import("./pages/restaurantAdmin/business_intelligence/anagrafica_articolo/InserimentoArticolo"));
const Calendario = lazy(() => import("./pages/restaurantAdmin/business_intelligence/Calendario"));
const Ruoli = lazy(() => import("./pages/restaurantAdmin/business_intelligence/Ruoli"));
const Collaboratori = lazy(() => import("./pages/restaurantAdmin/business_intelligence/Collaboratori"));
const Giacenza = lazy(() => import("./pages/restaurantAdmin/business_intelligence/Giacenza"));
const Scarti = lazy(() => import("./pages/restaurantAdmin/business_intelligence/Scarti"));
const Fattura = lazy(() => import("./pages/amministrazione/gestione_fatture/Fattura"));
const Ricevuta = lazy(() => import("./pages/amministrazione/gestione_ricevute/GestioneRicevutePage"));
const TabellaAnagraficaFornitore = lazy(() => import("./pages/restaurantAdmin/business_intelligence/gestione_fornitore/TabellaAnagraficaFornitore"));
const MenuListView = lazy(() => import("./pages/restaurantAdmin/business_intelligence/menu/MenuListView"));
const SezioneAggiuntivaListLayout = lazy(() => import("./pages/restaurantAdmin/business_intelligence/SezioneAggiuntivaListLayout"));
const PrenotazioneCassa = lazy(() => import("./pages/cassa/prenotazioni/PrenotazioneCassa"));
const InformazioniSection = lazy(() => import("./pages/restaurantAdmin/informazioni_section/InformazioniSection"));
const Pagamenti = lazy(() => import("./pages/cassa/pagamenti/pagamenti"));
const MappaSection = lazy(() => import("./pages/restaurantAdmin/mappa_section/mappa_section"));
const OrariAperturaPage = lazy(() => import("./pages/restaurantAdmin/orari/OrariApertura"));
const TavoloListView = lazy(() => import("./pages/restaurantAdmin/tavoli/TavoloListView"));
const GestioneStampantiTable = lazy(() => import("./pages/restaurantAdmin/gestionestampanti/GestioneStampantiTable"));
const DeliverectRegistrationInfoLayout = lazy(() => import("./pages/restaurantAdmin/deliverectregistrationinfo/DeliverectRegistrationInfoLayout"));
const TilbyRegistrationInfoLayout = lazy(() => import("./pages/restaurantAdmin/tilbyregistrationinfo/TilbyRegistrationInfoLayout"));
const EmailReportingSettings = lazy(() => import("./pages/restaurantAdmin/emailReporting/EmailReportingSettings"));
const ProfitLossDashboard = lazy(() => import("./pages/restaurantAdmin/business_intelligence/profitLoss/ProfitLossDashboard"));
const AreaIncassi = lazy(() => import("./pages/restaurantAdmin/business_intelligence/area_incassi/AreaIncassi"));
const AreaTempisticheSection = lazy(() => import("./pages/restaurantAdmin/business_intelligence/area_tempistiche/AreaTempistiche"));
const AreaPiattiSection = lazy(() => import("./pages/restaurantAdmin/business_intelligence/area_piatti/AreaPiatti"));
const AreaStaff = lazy(() => import("./pages/restaurantAdmin/business_intelligence/area_staff/AreaStaff"));
const Pass = lazy(() => import("./pages/passnuova/Pass"));
const Cucina = lazy(() => import("./pages/cucinanuova/Cucina"));
const CameriereTavoli = lazy(() => import("./pages/cassa/cassaTavoli/cameriereTavoli"));
const SuccessSend = lazy(() => import("./pages/ordinazione/cassa/success_send"));

interface Props extends ProviderContext { }
interface State { }

class App extends React.Component<Props, State> implements app_singleton.Observer_snackbar {
    constructor(props: Props) {
        super(props);
        this.state = {
            confirm_popup_open: undefined,
        };
    }

    componentDidMount() {
        app_singleton.event.snackbar.attach(this);
    }

    componentWillUnmount() {
        app_singleton.event.snackbar.detach(this);
    }

    update_snackbar(obj: app_singleton.snackbar_data): void {
        this.props.enqueueSnackbar(obj.text, {
            variant: obj.variant,
            style: { zIndex: 9999, padding: obj.top ? "0px 0px 100px 0px" : "" },
        });
    }

    render() {
        return (
            <EventTrackerProvider
                options={{
                    sendEvents: async (events) => {
                        await TrackedEventService.saveTrackedEvents(events);
                    },
                    flushInterval: 45000,
                    bufferSize: 20
                }}
            >

                <Stack sx={{ width: "100%", height: "100%" }}>
                    <ThemeProvider>
                        <Confirm_popup />
                        <PopupPrenotazione />
                        <LoadingPopup />
                        <Close_popup />
                        <BrowserRouter>
                            {/* <CompatRouter>
                                <Switch>
                                    <CompatRoute exact path="/guida">
                                        <Redirect push to="/guida/index.html" />
                                    </CompatRoute>
                                </Switch>
                            </CompatRouter> */}
                            <AuthProvider>
                                <Routes>
                                    <Route path="/" element={<VerticalNavBar />}>
                                        <Route index element={<Navigate to="login" replace />} />
                                        <Route path="login" element={<Login />} />
                                        <Route path="chat/:idRistorante" element={<ProtectedRoute />}>
                                            <Route path="" element={<ChatLayout />} />
                                        </Route>
                                        <Route path="myAccount/:idRistorante" element={<ProtectedRoute />}>
                                            <Route index element={<Navigate to="anagraficaArticoli" replace />} />
                                            <Route path="anagraficaArticoli" element={<TabellaAnagraficaArticolo />} />
                                            <Route path="articolo/:idArticolo" element={<InserimentoArticolo />} />
                                            <Route path="calendario" element={<Calendario />} />
                                            <Route path="ruoli" element={<Ruoli />} />
                                            <Route path="collaboratori" element={<Collaboratori />} />
                                            <Route path="giacenza" element={<Giacenza />} />
                                            <Route path="scarti" element={<Scarti />} />
                                            <Route path="fatture" element={<Fattura />} />
                                            <Route path="ricevute" element={<Ricevuta />} />
                                            <Route path="anagraficaFornitori" element={<TabellaAnagraficaFornitore />} />
                                            <Route path="menu" element={<MenuListView />} />
                                            <Route path="sezioniAggiuntive" element={<SezioneAggiuntivaListLayout />} />
                                            <Route path="prenotazioni" element={<PrenotazioneCassa />} />
                                            <Route path="*" element={<Navigate to="anagraficaArticoli" replace />} />
                                        </Route>
                                        <Route path="impostazioni/:idRistorante" element={<ProtectedRoute />}>
                                            <Route index element={<Navigate to="info" replace />} />
                                            <Route path="info" element={<InformazioniSection />} />
                                            <Route path="payment" element={<Pagamenti />} />
                                            <Route path="map" element={<MappaSection />} />
                                            <Route path="timetables" element={<OrariAperturaPage />} />
                                            <Route path="tables" element={<TavoloListView />} />
                                            <Route path="managePrinters" element={<GestioneStampantiTable />} />
                                            <Route path="emailReporting" element={<EmailReportingSettings />} />
                                            <Route path="deliverectIntegrazione" element={<DeliverectRegistrationInfoLayout />} />
                                            <Route path="tilbyIntegrazione" element={<TilbyRegistrationInfoLayout />} />
                                            <Route path="caIncloudIntegrazione" element={<CassaincloudRegistrationInfoLayout />} />
                                            <Route path="*" element={<Navigate to="info" replace />} />
                                        </Route>
                                        <Route path="bi/:idRistorante" element={<ProtectedRoute />}>
                                            <Route index element={<Navigate to="pl" replace />} />
                                            <Route path="pl" element={<ProfitLossDashboard />} />
                                            <Route path="incassi" element={<AreaIncassi />} />
                                            <Route path="tempistiche" element={<AreaTempisticheSection />} />
                                            <Route path="piatti" element={<AreaPiattiSection />} />
                                            <Route path="staff" element={<AreaStaff />} />
                                            <Route path="*" element={<Navigate to="pl" replace />} />
                                        </Route>
                                        <Route path="servizio/:idRistorante" element={<ProtectedRoute permissions={["abilitaModuloServizio"]} />}>
                                            <Route index element={<Navigate to="cameriere" replace />} />
                                            <Route path="pagamenti" element={<Pagamenti />} />
                                            <Route path="pass" element={<Pass />} />
                                            <Route path="cucina" element={<Cucina />} />
                                            <Route path="cameriere" element={<CameriereTavoli />} />
                                            <Route path="cameriere/success" element={<SuccessSend />} />
                                            <Route path="*" element={<Navigate to="cameriere" replace />} />
                                        </Route>
                                    </Route>
                                </Routes>
                            </AuthProvider>
                        </BrowserRouter>
                    </ThemeProvider>
                </Stack>
            </EventTrackerProvider>
        );
    }
}

export default withSnackbar<Props>(App);
